import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, Orbit, Sparkles, Loader2, MapPin, Clock, Info, ArrowRight, Flame, Droplets, Wind, Mountain, User } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANET_ICONS = {
  sun: '☉', moon: '☽', mercury: '☿', venus: '♀', mars: '♂',
  jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇'
};

const ELEMENT_ICONS = {
  'Feu': Flame,
  'Eau': Droplets,
  'Air': Wind,
  'Terre': Mountain
};

const ELEMENT_COLORS = {
  'Feu': '#FF4444',
  'Eau': '#2196F3',
  'Air': '#FFEB3B',
  'Terre': '#4CAF50'
};

const PlanetCard = ({ planetKey, planet, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`
        bg-white rounded-xl border border-sky-200 overflow-hidden
        hover:border-sky-300 transition-all cursor-pointer
        ${expanded ? 'ring-1 ring-cyan-500/40' : ''}
      `}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl" style={{ color: planet.color }}>{planet.symbol}</span>
            <div>
              <h4 className="font-semibold text-gray-900">{planet.name}</h4>
              <p className="text-sm text-gray-600">{planet.domain}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1">
              <span className="text-lg" style={{ color: planet.sign?.color }}>{planet.sign?.symbol}</span>
              <span className="text-gray-900 font-medium">{planet.sign?.name}</span>
            </div>
            <p className="text-xs text-gray-500">Maison {planet.house}</p>
          </div>
        </div>
        
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 pt-4 border-t border-sky-200"
          >
            <p className="text-sm text-gray-700 mb-3">{planet.psychological}</p>
            
            <div className="flex flex-wrap gap-2 mb-3">
              {planet.keywords?.map((kw, i) => (
                <span key={i} className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                  {kw}
                </span>
              ))}
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-sky-600 italic">"{planet.question}"</p>
            </div>
            
            {planet.retrograde && (
              <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                <span>℞</span> Rétrograde - Énergie intériorisée
              </p>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const ElementBar = ({ element, percentage, count }) => {
  const Icon = ELEMENT_ICONS[element] || Mountain;
  const color = ELEMENT_COLORS[element] || '#888';
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 w-20">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-sm text-gray-700">{element}</span>
      </div>
      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8 }}
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
        />
      </div>
      <span className="text-sm text-gray-600 w-12 text-right">{percentage}%</span>
    </div>
  );
};

const AstrozodiacPlanetsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/planets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success('Planètes calculées !');
      } else {
        toast.error(data.detail || 'Erreur de calcul');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/planets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chart_data: result,
          focus: 'planets',
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/astrozodiac" className="flex items-center gap-2 text-sky-600 hover:text-sky-500" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Orbit className="h-5 w-5 text-purple-400" />
            <span className="font-bold text-gray-900">Forces Planétaires</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Form */}
        {!result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30 mb-4">
                <Orbit className="h-10 w-10 text-purple-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Tes Forces Planétaires</h1>
              <p className="text-gray-600">10 planètes = 10 facettes de ta personnalité</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Ton prénom" className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-firstname" />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input type="date" value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthdate" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Heure (optionnel)</Label>
                <Input type="time" value={formData.birthTime} onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthtime" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville *</Label>
                <Input type="text" value={formData.birthCity} onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  placeholder="Paris" className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-city" />
              </div>
              
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-gray-900 font-semibold rounded-xl py-6"
                data-testid="calculate-btn">
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Calcul...</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser mes planètes</>}
              </Button>
            </form>
          </motion.div>
        )}
        
        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Forces Planétaires de {formData.firstName}</h2>
              <p className="text-gray-600">Clique sur une planète pour plus de détails</p>
            </div>
            
            {/* Element Distribution */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span className="text-2xl">⚡</span> Équilibre Élémentaire
              </h3>
              <div className="space-y-3">
                {result.element_distribution && Object.entries(result.element_distribution.percentages || {}).map(([element, pct]) => (
                  <ElementBar key={element} element={element} percentage={pct} count={result.element_distribution.counts?.[element]} />
                ))}
              </div>
              <p className="mt-4 text-sm text-sky-600">
                Dominant : <span className="font-semibold">{result.element_distribution?.dominant}</span>
                {result.element_distribution?.lacking && <> | À développer : <span className="font-semibold">{result.element_distribution.lacking}</span></>}
              </p>
            </div>
            
            {/* Planets Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.planets && Object.entries(result.planets).map(([key, planet], index) => (
                <PlanetCard key={key} planetKey={key} planet={planet} index={index} />
              ))}
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-600" /> Interprétation IA
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none"><ReactMarkdown>{interpretation}</ReactMarkdown></div>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={getInterpretation} disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-cyan-400 hover:to-blue-400 text-gray-900 rounded-xl"
                    data-testid="get-interpretation-btn">
                    {loadingInterpretation ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Obtenir l'interprétation IA</>}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" onClick={() => { setResult(null); setInterpretation(null); }}
                className="border-gray-200 text-gray-700 hover:bg-white rounded-xl" data-testid="new-calc-btn">
                Nouveau calcul
              </Button>
              <Button onClick={() => navigate('/astrozodiac/houses')}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-400 hover:to-teal-400 text-gray-900 rounded-xl" data-testid="go-houses-btn">
                Voir mes maisons <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacPlanetsPage;
